<script setup lang="ts">
import client from "@/utils/axios";
import { defineEmits, defineProps, reactive } from "vue";
import SelectMenu from "./SelectMenu.vue";
import { ref } from "vue";
import { errorToast } from "@/utils/custom-toast";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  input: any;
  step: any;
  isOpen: boolean;
  project: string | string[];
  phase: number;
  termins?: Array<any>;
  groupIndex?: number;
  inputIndex?: number;
};

type Emits = {
  (event: "toggleModal"): void;
  (event: "refresh"): void;
};

const emits = defineEmits<Emits>();
const props = defineProps<Props>();
const { t } = useTranslate();
const error = ref("");

const state = reactive({
  value:
    props.input.type === "repeatable_input"
      ? props.input.value[props.inputIndex].value
      : (props.input.type === "select" ||
          props.input.type === "static_select") &&
        props.input.options &&
        props.input.value
      ? Number(props.input.value)
      : props.input.value,
});

const options = props.termins?.map((item: any) => {
  return {
    name: item.name,
    id: item.id,
  };
});

const termin = ref(
  options?.find((item: any) => item.id === props.input.terminId)
);

const onSubmit = async () => {
  if (props.project && props.phase && props.input && termin.value) {
    if (props.input.type === "number") {
      if (Number(state.value) > 7) {
        error.value = t(
          "This field is conditional and value cannot exceed 7."
        ).value;
        return;
      } else {
        error.value = "";
      }
    }

    let payload = {
      terminId: termin.value?.id,
      stepId: props.step?.id,
      inputId: props.input?.id,
      inputIndex: props.inputIndex,
      groupIndex: props.groupIndex,
      value:
        props.input.type === "select" ||
        props.input.subtype === "select" ||
        props.input.type === "static_select" ||
        props.input.subtype === "static_select"
          ? Number(state.value)
          : state.value,
    };

    try {
      await client.post(
        `/projects/${props.project}/phase/${props.phase}/update-input`,
        payload
      );

      emits("refresh");
      emits("toggleModal");
    } catch (error: any) {
      errorToast(error);
    }
  } else {
    errorToast(t("global.all_fields_required").value);
  }
};
</script>

<template>
  <div
    class="fixed overflow-y-auto inset-0 bg-black bg-opacity-80 justify-center items-center z-50 transition-opacity duration-300 ease-in-out"
    :class="`${isOpen ? 'flex' : 'hidden'}`"
  >
    <div
      class="absolute overflow-y-auto top-5 w-[630px] p-6 mx-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-lg max-w-md"
    >
      <div
        class="flex items-center justify-between border-b border-gray-300 pb-3"
      >
        <h4
          class="text-xl leading-[32px] tracking-[0.01rem] text-primary-text-color font-semibold"
        >
          {{ t("phase.input") }}
        </h4>
        <button @click="emits('toggleModal')">
          <img src="@/assets/close-icon-2.svg" alt="Close icon" />
        </button>
      </div>
      <!-- Form -->
      <form @submit.prevent="onSubmit">
        <div>
          <h4
            class="text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-4"
          >
            {{ t("phase.question") }}
          </h4>
          <div class="mt-3">
            <div
              class="pointer-events-none w-full outline-none text-base text-primary-text-color font-normal tracking-[0.01rem] bg-[#F9F8F6] p-3 border border-[#E3E1EA] rounded-[5px] placeholder:text-primary-text-color"
            >
              <span>{{ input.name }}</span>
              <span v-if="input.type === 'repeatable_input'">
                / {{ input.repeatableInputNoun }}
                {{ inputIndex !== undefined ? inputIndex + 1 : "" }}
              </span>
            </div>
          </div>
        </div>
        <div>
          <h4
            class="text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-4"
          >
            {{ t("phase.answer") }}
          </h4>
          <div
            v-if="input.type === 'text' || input.subtype === 'text'"
            class="mt-3"
          >
            <textarea
              v-model="state.value"
              rows="4"
              class="w-full outline-none text-base text-primary-text-color font-normal tracking-[0.01rem] bg-[#F9F8F6] p-3 border border-[#E3E1EA] rounded-[5px] resize-none placeholder:text-primary-text-color"
            ></textarea>
          </div>
          <div v-if="input.type === 'number'" class="mt-3 flex flex-col">
            <input
              v-model="state.value"
              class="w-full outline-none text-base text-primary-text-color font-normal tracking-[0.01rem] bg-[#F9F8F6] p-3 border border-[#E3E1EA] rounded-[5px] placeholder:text-primary-text-color"
              type="number"
            />
            <p v-if="error" class="text-sm text-red-400 font-medium mt-1">
              {{ error }}
            </p>
          </div>
          <div
            v-if="
              input.type === 'select' ||
              input.subtype === 'select' ||
              input.type === 'static_select' ||
              input.subtype === 'static_select'
            "
            class="mt-3 flex flex-col gap-3"
          >
            <div
              v-for="option in input.options"
              :key="option.id"
              class="flex items-center gap-2"
            >
              <div class="flex items-center justify-center w-4 h-4 relative">
                <input
                  v-if="Number(state.value) !== option.id"
                  v-model="state.value"
                  :id="option.id"
                  :value="option.id"
                  type="radio"
                  class="rounded-full w-full h-full border-2 border-[#393840]"
                />
                <img
                  v-if="state.value === option.id"
                  class="cursor-pointer w-full h-full object-cover"
                  src="@/assets/rounded-selected-checkbox.svg"
                  alt=""
                />
              </div>
              <label>{{ option.option }}</label>
            </div>
          </div>
          <div v-if="!input.terminId">
            <h4
              class="text-base leading-[26px] tracking-[0.01rem] text-[#898790] font-medium pt-4"
            >
              {{ t("phase.on_which_termin_picture_taken") }}
            </h4>
            <SelectMenu :options="options" v-model="termin" />
          </div>
        </div>
        <!-- Submit button -->
        <div class="flex justify-end">
          <input
            type="submit"
            class="mt-5 flex items-center justify-center py-3 px-[48px] text-base text-white bg-[#393840] tracking-[0.01rem] font-semibold outline-none rounded-primary-radius border-[2px] border-[#393840] cursor-pointer hover:bg-[#28272c] duration-200"
            value="Save"
          />
        </div>
      </form>
    </div>
  </div>
</template>
